import React from 'react';
import Modal from 'components/Modal';
import styled from 'styled-components';
import { TYPE } from 'theme';
import VerificationInput from 'react-verification-input';
import { useInviteCode } from 'hooks/useInvite';
import user from 'stores/user';
import { getOnePieceServerApi } from 'gql-ops';
import Button from 'components/Button';

const ModalContent = styled.div`
    position: relative;
    width: 330px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    ${({ theme }) => theme.mediaWidth.upToSmall`
        width: 90%;
    `};
`;

const InviteCodeWrapper = styled.div`
    position: relative;
    padding-bottom: 30px;

    .vi__container {
        width: auto;
        height: 48px;
        justify-content: space-between;

        .vi__character {
            width: 48px;
            height: 48px;
            line-height: 48px;
            font-size: 24px;
            font-weight: 400;
            border: none;
            border-radius: 8px;
            background-color: #fff;
            &.vi__character--selected {
                outline: 2px solid ${({ theme }) => theme.c.primary01};
                color: #000;
            }
            &.invalid {
                border: 1px solid #ff6060;
            }
        }
    }
    ${({ theme }) => theme.mediaWidth.upToSmall`
        .vi__container {
            gap: 10px;

            .vi__character {
                width: 48px;
                height: 48px;
                line-height: 48px;
                border: none;
                border-radius: 8px;
            }
        }
    `};
`;
const InvalidMsg = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
`;

const InvitorWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #fff;
    color: #000;
    border-radius: 8px;
    width: 100%;
    height: 48px;
    font-size: 16px;
`;

const SaveButton = styled(Button)`
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 12px;
    width: 100%;
    height: 48px !important;
    font-size: 20px !important;
    margin-top: 30px;
`;

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
}

function maskAddress(address?: string) {
    if (!address) return 'N/A';

    return address.slice(0, 6) + '...' + address.slice(-4);
}

const InviteCodeModalContent = ({ isOpen, onClose }: ModalProps) => {
    const [inviteCode, setInviteCode, inviter, invalidMsg] = useInviteCode();

    const handleBindInviteCode = async () => {
        if (!inviteCode) return;
        await getOnePieceServerApi(true).bindInviteCode({
            inviteCode,
            address: user.address.toLowerCase(),
        });
        await user.refreshOpsUserInfo();
    };

    const onBindInviteCode = async () => {
        await handleBindInviteCode();
        onClose();
    };

    return (
        <ModalContent>
            <TYPE.main fontSize={20} fontWeight={600} marginBottom="20px" marginTop="20px">
                Invitation Code
            </TYPE.main>
            <InviteCodeWrapper>
                <VerificationInput
                    length={6}
                    placeholder=""
                    value={inviteCode}
                    onChange={setInviteCode}
                    classNames={{ character: invalidMsg ? 'invalid' : '' }}
                />
                {invalidMsg && (
                    <InvalidMsg>
                        <TYPE.desc fontSize={14} color="#FF6060" alignSelf="flex-start">
                            {invalidMsg}
                        </TYPE.desc>
                    </InvalidMsg>
                )}
            </InviteCodeWrapper>
            <TYPE.desc fontSize={16} marginBottom={20} marginTop={10}>
                Your Invitor
            </TYPE.desc>
            <InvitorWrapper>{inviter ? maskAddress(inviter.walletAddress) : ''}</InvitorWrapper>
            <SaveButton
                type="gradient"
                disabled={inviteCode.length < 6 || !user.address || !!invalidMsg || !inviter}
                onClick={onBindInviteCode}
            >
                Save
            </SaveButton>
        </ModalContent>
    );
};

const InviteCodeModal = (props: ModalProps) => {
    return (
        <Modal {...props} shadowProps>
            <InviteCodeModalContent {...props} />
        </Modal>
    );
};

export default InviteCodeModal;
